/**
 * "This task has gone quiet" — the console-side smell test for an
 * orphaned task.
 *
 * WHY. `patrol.dispatch-48` read `active` on this screen for 45 h
 * after the fleet adapter that held it was replaced. Nothing on the
 * row said otherwise: the status chip pulsed, the Duration column
 * counted up, and the Cancel button looked live. The one thing that
 * WAS visibly wrong the whole time is that no update had arrived
 * since 2026-08-28 13:44 — so say that on the row.
 *
 * WHAT WE MEASURE. The projection envelope's
 * `projector_received_at_ms` — when the platform last published
 * anything about this task — carried onto `TaskStateView.lastUpdateMs`
 * by `adaptTaskSummary` from both paths that reach the screens (the
 * zenoh REST seed in `v1-zenoh-client.ts::fetchTasksSnapshot`, and the
 * live reducer's `ReducerState.receivedAt`).
 *
 * NOT the task's own timestamps. `started_at_ms` is when the work
 * BEGAN, so silence measured from it grows for as long as the task
 * runs: a Phantas cleaning run or an auto-generated charge task at
 * sg-nomad-01 routinely passes 2 h while publishing every second, and
 * badging those trains operators to ignore the one signal meant to
 * contradict a healthy-looking chip. The only case where a payload
 * timestamp is still used is a task that never started at all (the
 * `Chargee4d3cb` case: queued since 2026-08-26, never assigned) and
 * whose record reached us without an envelope — there `submitted_at_ms`
 * IS the last thing that happened. A started task with no envelope
 * gets no badge rather than a guessed one.
 */
import type { TaskState } from '../../api/rmf-client';

/** Below this, a task is simply running; above it, say something. */
export const QUIET_TASK_AFTER_MS = 2 * 60 * 60 * 1000;

/**
 * Only these three end a task. `queued` and `interrupted` are NOT here
 * on purpose, and this is a deliberate divergence from the worker's
 * `_REAPABLE_LIVE_TASK_STATES`, which excludes `queued` because silence
 * is normal for a task nobody has started. Here silence is still worth
 * saying out loud: `Chargee4d3cb` sat `queued` and unassigned from
 * 2026-08-26 to 2026-08-30, and a fire-mode `interrupted` task that
 * stops publishing is precisely the thing an operator must not mistake
 * for "paused and fine". The badge only says how old the record is —
 * it never reaps — so being wider than the reaper costs nothing.
 */
const TERMINAL: ReadonlySet<TaskState['status']> = new Set([
  'completed',
  'failed',
  'cancelled',
]);

export function isTerminalTask(status: TaskState['status']): boolean {
  return TERMINAL.has(status);
}

/**
 * The newest moment this record proves anything happened, or null when
 * it carries no usable timestamp at all.
 */
export function lastKnownActivityMs(task: TaskState): number | null {
  if (typeof task.lastUpdateMs === 'number' && Number.isFinite(task.lastUpdateMs)) {
    return task.lastUpdateMs;
  }
  // No envelope came with this record. `submitted_at_ms` is a true
  // last-activity time ONLY while nothing has happened since; the
  // moment a task starts, measuring from a payload timestamp
  // over-states silence for every healthy long-running job, so say
  // nothing instead.
  if (
    task.startedAt === undefined &&
    typeof task.submittedAtMs === 'number' &&
    Number.isFinite(task.submittedAtMs)
  ) {
    return task.submittedAtMs;
  }
  return null;
}

/**
 * Whole hours of silence on a NON-terminal task, or null when the task
 * is terminal, has no timestamp, or has not been quiet long enough to
 * be worth a badge. A terminal task is meant to stop publishing.
 */
export function quietHours(task: TaskState, now: number = Date.now()): number | null {
  if (isTerminalTask(task.status)) return null;
  const last = lastKnownActivityMs(task);
  if (last === null) return null;
  const elapsed = now - last;
  // A clock skew that puts the task in the future is not staleness.
  if (elapsed < QUIET_TASK_AFTER_MS) return null;
  return Math.floor(elapsed / (60 * 60 * 1000));
}

/**
 * How to read a `synthetic: true` failure. The platform's stall reaper
 * wrote it because nothing could ever answer for the task again — the
 * robot did not fail, so the copy must not say it did.
 */
export function syntheticClosureText(task: TaskState): string | null {
  if (!task.synthetic) return null;
  const reason = task.failureReason?.trim();
  return reason
    ? `Closed by the platform: ${reason}`
    : 'Closed by the platform — no robot reported this task as failed.';
}
